import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Route, withRouter, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import Layout from '../../Layout';

import { changeDate } from '../store/actions';
import { handleLogout, setSingleEstablishment, setInitialEstablishments, setAppPermissionEstablishments } from '../../../pages/Login/store/actions';
import { setEstablishmentId } from '../../../pages/BusinessInfo/store/actions';

import { formatEstablishments } from '../../../helpers/formatters';
import { SITE_RESTRICTIONS_TO_PAGES } from 'helpers/restrictions';

import { setToken } from '../../../services';

const currentDate = moment().format('YYYY-MM-DD');
const isBeforeCurrentDate = date => moment(currentDate).isAfter(date, 'day');

const LayoutRoute = ({ component: Component, singleEstablishment, path, ...rest }) => {
  const dispatch = useDispatch();

  const history = useHistory();

  const { userRestrictions, submit: { isAdminUser } } = useSelector(state => state.profile);
  const { establishmentId } = useSelector(state => state.businessInfo);
  const { endDate } = useSelector(state => state.dateFilter);
  const { token, loggedIn, tokenExpirationTime, establishments } = useSelector(state => state.login);
  const situationEstablishments = useSelector(state => state.login.situation.establishments);

  const [isSetupComplete, setIsSetupComplete] = useState(false);
  const [isLoginFromNoAccessCode] = useState(!!history.location.state?.data?.noAccessCode);

  const filterEstablishments = formatEstablishments(establishments, +isAdminUser == 1);

  const isSelfService = history.location.pathname == '/autoatendimento';

  const selfServiceEstablishmentsId = situationEstablishments
    ?.filter(establishment => establishment?.establishmentTypes?.map(Number)?.includes(1))
    ?.map(establishment => establishment?.establishmentId);

  const routeEstablishments = isSelfService
    ? filterEstablishments?.filter(establishment => selfServiceEstablishmentsId?.includes(establishment?.value))
    : filterEstablishments;

  const availableEstablishmentsId = establishments
    ?.map(establishment => establishment?.establishmentId);

  const blockedRoutes = SITE_RESTRICTIONS_TO_PAGES
    ?.filter(restriction => userRestrictions?.includes(restriction?.restriction))
    ?.map(restriction => restriction?.page);

  const verifyTokenExpired = () => {
    return !!(tokenExpirationTime && moment().isAfter(tokenExpirationTime));
  }

  const redirectToLogin = () => {
    dispatch(handleLogout());
    return () => history.push('/');
  }

  useEffect(() => {
    (async () => {
      if(+isAdminUser != 1) {
        if(blockedRoutes?.includes(history.location.pathname)) {
          toast.error('Você não tem autorização para acessar esta área.');
          return history.push('/visao-geral');
        }

        const setValidRoutes = async () => {
          const premiumPackageRoutes = ['/funcionarios', '/comissionamento', '/comissoes', '/agendamento', '/notas-fiscais', '/configuracoes-de-notas-fiscais'];

          let establishments = situationEstablishments;

          if(premiumPackageRoutes?.includes(history.location.pathname)) {
            establishments = situationEstablishments?.filter(establishment => +establishment?.premiumPackage == 1);
          }

          return establishments;
        }

        const establishments = await setValidRoutes();
        dispatch(setAppPermissionEstablishments(establishments, isAdminUser));
      }
    })();
  }, [history.location.pathname]);

  useEffect(() => {
    const availableEstablishmentsIdWithEmptyEstablishment = availableEstablishmentsId?.includes('')
      ? availableEstablishmentsId
      : [...availableEstablishmentsId, ''];

    if(!availableEstablishmentsIdWithEmptyEstablishment?.includes(establishmentId) && !singleEstablishment) {
      dispatch(setEstablishmentId({ establishmentId: '' }));
    }

    if(!availableEstablishmentsIdWithEmptyEstablishment?.includes(establishmentId) && singleEstablishment) {
      dispatch(setEstablishmentId({ establishmentId: availableEstablishmentsId[0] }));
    }
  }, [availableEstablishmentsId]);

  useEffect(() => {
    if(isSelfService && routeEstablishments?.length > 0) {
      const isEstablishmentAvailable = routeEstablishments?.some(establishment => establishment?.value == establishmentId);

      if(!isEstablishmentAvailable) {
        dispatch(setEstablishmentId({ establishmentId: routeEstablishments[0]?.value }));
      }
    }
  }, [isSelfService, establishmentId, routeEstablishments?.length]);

  useEffect(() => {
    if(isLoginFromNoAccessCode) {
      const accessToken = token || history.location.state?.data?.token;

      if(accessToken) {
        setToken(accessToken);
      }

      setIsSetupComplete(true);
    }
  }, [isLoginFromNoAccessCode, token]);

  useEffect(() => {
    if(history.location.pathname != '/suas-assinaturas') {
      if(isLoginFromNoAccessCode) {
        dispatch(handleLogout());
        toast.error('Ação não autorizada.');
        return history.push('/');
      }
    }
  }, [isLoginFromNoAccessCode, history.location.pathname]);

  useEffect(() => {
    if(!isLoginFromNoAccessCode) {
      if(isBeforeCurrentDate(endDate)) {
        dispatch(changeDate(currentDate, currentDate));
      }

      if(
        filterEstablishments.length === 0 ||
        (filterEstablishments.length === 1 && filterEstablishments[0].establishmentName === 'Todos os estabelecimentos')
      ) {
        history.push('/');
      }
    }
  }, []);

  useEffect(() => {
    if(!isLoginFromNoAccessCode) {
      if(!loggedIn) {
        return redirectToLogin();
      }

      if(verifyTokenExpired()) {
        return redirectToLogin();
      }

      if(token) {
        setToken(token);
      }

      setIsSetupComplete(true);
    }
  }, [loggedIn, verifyTokenExpired()]);

  useEffect(() => {
    if(!isLoginFromNoAccessCode) {
      const filterAllEstablishmentsOption = filterEstablishments.filter(e => !e.value);
      const filterRestEstablishments = filterEstablishments.filter(e => e.value);

      if(
        singleEstablishment &&
        !establishmentId &&
        filterRestEstablishments.length > 0 &&
        filterAllEstablishmentsOption.length === 0
      ) {
        dispatch(setSingleEstablishment(filterRestEstablishments));

        dispatch(
          setEstablishmentId({
            establishmentId: filterRestEstablishments[0].value,
          })
        );
      }

      if(singleEstablishment && filterAllEstablishmentsOption.length > 0) {
        const firstEstablishmentId = establishmentId || filterEstablishments[1]?.value;

        dispatch(setSingleEstablishment(filterEstablishments));
        dispatch(setEstablishmentId({ establishmentId: firstEstablishmentId }));
      }

      if(
        !singleEstablishment &&
        filterEstablishments.length > 1 &&
        filterAllEstablishmentsOption.length === 0
      ) {
        dispatch(setInitialEstablishments(filterEstablishments));
      }
    }
  }, [filterEstablishments, establishmentId, singleEstablishment]);

  return(
    <Layout filterEstablishments={routeEstablishments} {...rest}>
      {isSetupComplete && (
        <Route
          path={path}
          render={matchProps => <Component {...matchProps} />}
        />
      )}
    </Layout>
  );
}

LayoutRoute.defaultProps = {
  singleEstablishment: false
};

LayoutRoute.propTypes = {
  singleEstablishment: PropTypes.bool,
  component: PropTypes.element.isRequired,
  path: PropTypes.string.isRequired
};

export default withRouter(LayoutRoute);